import React from 'react';
import { CogIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import { Link } from 'react-router-dom';

const PerformanceFrameworkStage3 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 3: Agree & Align on How We'll Use Performance" icon={<CogIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Agree on what you'll actually do with your performance framework</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Define how performance will influence pay, promotion, growth, and development</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Document clear guidance on how performance assessment connects to key decisions</span>
            </li>
          </ul>
          <p className="mt-4 text-gray-300">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">From 'Why' to 'How'</h3>
        <p className="text-gray-300 mb-6">
          In Stage 1 you got alignment on 'why' you needed a performance framework. Now you need to agree on what you'll 
          actually <strong className="text-white">do</strong> with that framework—the decisions it will influence, how it fits into 
          growth, development and reward.
        </p>
        
        <p className="text-gray-300 mb-6">
          Performance will often mean (and serve) slightly different things in different companies.
        </p>

        <h4 className="text-lg font-semibold text-white mb-4">Key Questions To Ask Yourselves Now:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Why are we assessing performance? For what outcome? <em className="text-gray-400">(Remind yourself from Stage 1)</em></span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">What does "great performance" actually look like here?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Does this align in reality to competency frameworks and goal expectations?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Is this tied to pay, promotion, or purely feedback & growth?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Do we believe in things like A-Player culture? If so, how will performance cycles also influence potential bonus or exit conversations throughout the year?</span>
          </li>
        </ul>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">How Performance Should Influence Key Decisions</h3>
        
        <p className="text-gray-300 mb-6">
          You need documented guidance for everyone to make it clear on exactly <em>how</em> performance plays a role in influencing these areas:
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">Growth & Development</h4>
            <p className="text-gray-300 text-sm">How performance assessments guide learning opportunities, skill development, and career progression planning.</p>
          </div>
          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">Promotions</h4>
            <p className="text-gray-300 text-sm">Clear criteria linking performance levels to promotion readiness and advancement opportunities.</p>
          </div>
          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">Reward & Compensation</h4>
            <p className="text-gray-300 text-sm">How performance results influence salary reviews, bonuses, and other forms of recognition.</p>
          </div>
          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">Exit Conversations</h4>
            <p className="text-gray-300 text-sm">When and how performance issues lead to improvement plans or difficult conversations about fit.</p>
          </div>
        </div>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <p className="text-gray-300 text-sm">
            <strong className="text-white">Why This Matters:</strong> This really helps folks to understand more about 'why' we do this, 
            and transparently ground expectations across your business. This also helps when actually starting to design your framework, 
            which we'll dive into next!
          </p>
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Creating Your Performance Impact Framework</h3>
        
        <CopyToNotion title="Performance Impact Planning Template">
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-white mb-3">How We Use Performance at [Company Name]</h4>
              
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-white mb-2">Growth & Development</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm">Performance assessments help us identify:</p>
                    <ul className="mt-2 space-y-1 text-gray-300 text-sm">
                      <li>• Learning and development opportunities</li>
                      <li>• Skill gaps to address through training</li>
                      <li>• Stretch assignments and growth projects</li>
                      <li>• Mentoring and coaching needs</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-white mb-2">Promotions</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm">Promotion readiness requires:</p>
                    <ul className="mt-2 space-y-1 text-gray-300 text-sm">
                      <li>• [X] consecutive cycles of meeting expectations</li>
                      <li>• Demonstrated competency at next level</li>
                      <li>• Consistent behavioral alignment</li>
                      <li>• Business need and opportunity</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-white mb-2">Compensation</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm">Performance influences:</p>
                    <ul className="mt-2 space-y-1 text-gray-300 text-sm">
                      <li>• Annual salary review timing and amount</li>
                      <li>• Bonus eligibility and calculation</li>
                      <li>• Recognition and rewards programs</li>
                      <li>• Equity and long-term incentives</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-white mb-2">Performance Support</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm">When performance needs improvement:</p>
                    <ul className="mt-2 space-y-1 text-gray-300 text-sm">
                      <li>• Immediate manager support and coaching</li>
                      <li>• Performance improvement plan if needed</li>
                      <li>• Additional training and resources</li>
                      <li>• Clear timelines and expectations</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CopyToNotion>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h4 className="text-white font-semibold mb-3">By the end of this stage you should have:</h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Documented guidance for everyone on exactly how performance influences key decisions</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Clear alignment on how performance connects to growth, promotions, reward, and support</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Transparent expectations grounded across your business</span>
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary/30">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Next:</strong> Move to <Link to="/playbooks/how-to-build-launch-embed-performance/stage-4" className="text-primary hover:text-primary-light">Stage 4: Design your Performance Framework </Link> 
              to build the actual framework structure and assessment approach.
            </p>
          </div>
        </div>
      </PlaybookCard>

      <ExpandableCallout type="warning" title="Where You Might Face Resistance" defaultOpen={false}>
        <div className="space-y-4">
          <p className="text-gray-300">
            You might find that certain stakeholders have different views on what performance assessment should influence. 
            Some may care more about monetary reward for their top performers, others growth. You might find there's some 
            disagreement here in the team when trying to align on this.
          </p>
          <p className="text-gray-300">
            That's ok. You don't have to pick one singular thing necessarily, but do try to get clear on how it may 
            influence all of the above as part of your conversations.
          </p>
          <div className="bg-gray-800/50 p-4 rounded-lg border-l-4 border-primary-light">
            <p className="text-gray-300">
              <strong className="text-white">Pro tip:</strong> Focus on creating clarity rather than perfect consensus. 
              Different stakeholders can have different priorities as long as everyone understands how decisions will be made.
            </p>
          </div>
        </div>
      </ExpandableCallout>
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkStage3; 